import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigation } from "@repo/core/hooks/useNavigation";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { PasswordStrengthEnum } from "./components/PasswordStrength/types";
import { useEffect, useState } from "react";
import useToast from "@repo/core/components/core/Toast";
import { useDisableCopyPaste } from "@repo/core/hooks/useDisableCopyPaste";
import { useBreakpointValue } from "@gluestack-style/react";

const minNumbersRegex = /^[^0-9]*(?:[0-9][^0-9]*){4,}$/;
const minLowercaseRegex = /^[^a-z]*(?:[a-z][^a-z]*){2,}$/;

const formSchema = z
  .object({
    newPassword: z.string().min(6),
    confirmNewPassword: z.string()
  })
  .superRefine(({ newPassword, confirmNewPassword }, ctx) => {
    if (confirmNewPassword !== newPassword) {
      ctx.addIssue({
        code: "custom",
        message: "As senhas devem ser iguais",
        path: ["confirmNewPassword"]
      });
    }
  });

export type FormDataType = z.infer<typeof formSchema>;

export const ChangePasswordController = () => {
  const { push, back } = useNavigation();
  const { success } = useToast();
  const isMobile = useBreakpointValue({ base: true, md: false });

  const { onKeyPressDisablePaste, onKeyUpDisablePaste } = useDisableCopyPaste();
  const [passwordValidations, setPasswordValidations] = useState<{
    hasMinCharacters: boolean;
    hasMinNumbers: boolean;
    hasMinLowercaseLetters: boolean;
    validRequirementsCount: number;
    passwordStrength: PasswordStrengthEnum;
  }>({
    hasMinCharacters: false,
    hasMinNumbers: false,
    hasMinLowercaseLetters: false,
    validRequirementsCount: 0,
    passwordStrength: PasswordStrengthEnum.NONE
  });

  const {
    control,
    watch,
    formState: { isValid, isSubmitting },
    handleSubmit
  } = useForm<FormDataType>({
    resolver: zodResolver(formSchema)
  });

  const newPassword = watch("newPassword");

  const handleOnSubmitForm = handleSubmit(() => {
    success("Senha alterada com sucesso!");
    back();
  });

  const validatePasswordRequirements = () => {
    const isValidLength = newPassword?.length >= 6;
    const isValidMinNumbers = minNumbersRegex.test(newPassword);
    const isValidMinLowercase = minLowercaseRegex.test(newPassword);
    if (!passwordValidations.hasMinCharacters) {
      if (isValidLength) {
        setPasswordValidations((prevState) => {
          const totalValidRequirementsCount =
            prevState.validRequirementsCount + 1;

          return {
            ...prevState,
            hasMinCharacters: true,
            validRequirementsCount: totalValidRequirementsCount,
            passwordStrength: validatePasswordStrength(
              totalValidRequirementsCount
            )
          };
        });
      }
    } else {
      if (!isValidLength) {
        setPasswordValidations((prevState) => {
          const totalValidRequirementsCount =
            prevState.validRequirementsCount - 1;

          return {
            ...prevState,
            hasMinCharacters: false,
            validRequirementsCount: totalValidRequirementsCount,
            passwordStrength: validatePasswordStrength(
              totalValidRequirementsCount
            )
          };
        });
      }
    }

    if (!passwordValidations.hasMinNumbers) {
      if (isValidMinNumbers) {
        setPasswordValidations((prevState) => {
          const totalValidRequirementsCount =
            prevState.validRequirementsCount + 1;

          return {
            ...prevState,
            hasMinNumbers: true,
            validRequirementsCount: totalValidRequirementsCount,
            passwordStrength: validatePasswordStrength(
              totalValidRequirementsCount
            )
          };
        });
      }
    } else {
      if (!isValidMinNumbers) {
        setPasswordValidations((prevState) => {
          const totalValidRequirementsCount =
            prevState.validRequirementsCount - 1;

          return {
            ...prevState,
            hasMinNumbers: false,
            validRequirementsCount: totalValidRequirementsCount,
            passwordStrength: validatePasswordStrength(
              totalValidRequirementsCount
            )
          };
        });
      }
    }

    if (!passwordValidations.hasMinLowercaseLetters) {
      if (isValidMinLowercase) {
        setPasswordValidations((prevState) => {
          const totalValidRequirementsCount =
            prevState.validRequirementsCount + 1;

          return {
            ...prevState,
            hasMinLowercaseLetters: true,
            validRequirementsCount: totalValidRequirementsCount,
            passwordStrength: validatePasswordStrength(
              totalValidRequirementsCount
            )
          };
        });
      }
    } else {
      if (!isValidMinLowercase) {
        setPasswordValidations((prevState) => {
          const totalValidRequirementsCount =
            prevState.validRequirementsCount - 1;

          return {
            ...prevState,
            hasMinLowercaseLetters: false,
            validRequirementsCount: totalValidRequirementsCount,
            passwordStrength: validatePasswordStrength(
              totalValidRequirementsCount
            )
          };
        });
      }
    }
  };

  const validatePasswordStrength = (requirementCount: number) => {
    switch (requirementCount) {
      case 1:
        return PasswordStrengthEnum.WEAK;
      case 2:
        return PasswordStrengthEnum.MEDIUM;
      case 3:
        return PasswordStrengthEnum.STRONG;
      default:
        return PasswordStrengthEnum.NONE;
    }
  };

  useEffect(() => {
    if (newPassword) {
      validatePasswordRequirements();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newPassword]);

  const {
    hasMinCharacters,
    hasMinNumbers,
    hasMinLowercaseLetters,
    passwordStrength
  } = passwordValidations;

  const isValidPassword =
    isValid && hasMinCharacters && hasMinNumbers && hasMinLowercaseLetters;
  return {
    push,
    back,
    isMobile,
    control,
    isValidPassword,
    isSubmitting,
    handleOnSubmitForm,
    hasMinCharacters,
    hasMinNumbers,
    hasMinLowercaseLetters,
    passwordStrength,
    onKeyPressDisablePaste,
    onKeyUpDisablePaste
  };
};
